import { Component, ErrorInfo, ReactNode, type JSX } from 'react'
import i18n from '@/i18n'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in child component tree,
 * logs them, and displays a fallback UI instead of crashing.
 *
 * Usage:
 * <ErrorBoundary fallback={<ErrorFallback />}>
 *   <MyComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo)
    }

    // Call optional error handler
    this.props.onError?.(error, errorInfo)
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null })
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // Return custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default fallback UI
      return (
        <div className="flex min-h-[200px] flex-col items-center justify-center rounded-lg bg-gray-900 p-8 text-center">
          <div className="mb-4 text-4xl">⚠️</div>
          <h2 className="mb-2 text-xl font-semibold text-white">
            {i18n.t('errors.somethingWrong')}
          </h2>
          <p className="mb-4 text-gray-400">
            {this.state.error?.message || i18n.t('errors.unexpectedError')}
          </p>
          <button
            onClick={this.handleRetry}
            className="rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
          >
            {i18n.t('errors.tryAgain')}
          </button>
        </div>
      )
    }

    return this.props.children
  }
}

/**
 * Page-level Error Fallback
 * Shows when a page component fails
 */
export function PageErrorFallback(): JSX.Element {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-950 p-8 text-center">
      <div className="mb-4 text-6xl">😵</div>
      <h2 className="mb-2 text-2xl font-bold text-white">
        {i18n.t('errors.pageErrorTitle')}
      </h2>
      <p className="mb-4 max-w-md text-gray-400">
        {i18n.t('errors.pageErrorDesc')}
      </p>
      <div className="flex gap-4">
        <button
          onClick={() => window.location.reload()}
          className="rounded-lg bg-blue-600 px-6 py-2 text-white transition-colors hover:bg-blue-700"
        >
          {i18n.t('errors.reload')}
        </button>
        <a
          href="/"
          className="rounded-lg border border-gray-600 px-6 py-2 text-gray-300 transition-colors hover:border-gray-500"
        >
          {i18n.t('errors.home')}
        </a>
      </div>
    </div>
  )
}
